/**
 * ProjectSwitcher.tsx — переключатель проекта в боковом меню (WEB-340, вид —
 * WEB-3739 юнит 2 по `Switch` эталона `design/cabinet-rescin/sections/shell-x.jsx`,
 * стили `.qm-sw` / `.qm-pop` — `styles/cabinet-shell.css`).
 *
 * Кнопка `ctx` — значок (первая буква проекта или сетка у «Вся организация») и
 * подпись; окно `ctx-pop` (`role=menu`) сверху вниз:
 *   1. поиск «Найти проект…» (фокус сам — только шире 640: на телефоне без
 *      автоклавиатуры), подпись «Проекты» (с числом, если их больше 8);
 *   2. список проектов (до 280 с прокруткой) — `switchActiveProjectThunk` (BE-persist);
 *   3. «Все проекты» (администратор организации) — /projects;
 *   4. «Вся организация» — режим организации (WEB-499);
 *   5. «Создать проект» — видно всем, у не-администратора организации пункт
 *      погашен и называет причину строкой (WEB-1777 п.3, правило WEB-1556).
 * Мастер создания проекта живёт здесь (раньше — в шапке `Header.tsx`, снята
 * юнитом 2): закрытие с проектом перечитывает список (WEB-336) и ведёт по
 * режиму (WEB-377), вход в созданный проект спрашивает «Сохранить?» (WEB-2912).
 * Закрытие окна — щелчок мимо и Esc (`useOutside`), клавиатура — `useMenuKeys`.
 */
import React, { useEffect, useRef, useState } from 'react';
import { App } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon, useMenuKeys, useOutside } from '@/components/kit/ds';
import { CreateProjectWizard } from '@/components/context/CreateProjectWizard';
import { useAppDispatch, useAppSelector } from '@/hooks/hooks';
import { isOrgTopLevelPath } from '@/utils/orgContext';
import {
  clearActiveProjectThunk,
  fetchProjectsThunk,
  setCurrentProject,
  switchActiveProjectThunk,
} from '@/store/projectsSlice';
import { useOrgRole } from '@/hooks/useOrgRole';
import { PROJECT_LANDING, orgModeLanding } from '@/router/landingRoute';
import { createProjectDeniedHint } from '@/constants/projectCreateRights';
import { useStrings } from '@/i18n';
import { commonStrings } from '@/i18n/dictionaries/common';
import { settingsStrings } from '@/i18n/dictionaries/settings';
import { localizeError } from '@/utils/localizeError';
import { confirmLeave } from '@/utils/leaveGuard';
import type { Project } from '@/types';

/** Пункт меню нажимается и с клавиатуры (Enter, Пробел), если `useMenuKeys` его не взял. */
function keyAct(act: () => void) {
  return (e: React.KeyboardEvent) => {
    if (e.nativeEvent.defaultPrevented) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      act();
    }
  };
}

const phone = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(max-width: 640px)').matches;

export function ProjectSwitcher() {
  const s = useStrings(settingsStrings);
  const c = useStrings(commonStrings);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { projects, currentProject } = useAppSelector((state) => state.projects);
  // WEB-336: нужен для перечитывания списка с организацией после мастера.
  const activeOrgId = useAppSelector((state) => state.organizations.activeOrgId);
  const [open, setOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  // WEB-641 A3: поиск фильтрует список проектов; «Вся организация» — всегда видна.
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  useOutside(ref, () => setOpen(false), open);
  useMenuKeys(ref, open, setOpen);
  // После useMenuKeys (тот ставит фокус на первый пункт): фокус — в поле поиска.
  useEffect(() => {
    if (open && !phone()) search.current?.focus();
  }, [open]);
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const q = query.trim().toLowerCase();
  const filteredProjects = q
    ? projects.filter((p) => p.name.toLowerCase().includes(q))
    : projects;
  // WEB-445: «Создать проект» / «Все проекты» — действия УРОВНЯ ОРГАНИЗАЦИИ,
  // по орг-роли (Owner/Admin), а не по проектной.
  const { isOrgAdmin, role: orgRole } = useOrgRole();
  // WEB-650: тост через App.useApp() — темизирован.
  const { message } = App.useApp();

  // Случай PM 10.08: на орг-адресах выбрана «Вся организация» — меню рядом уже
  // в режиме организации по адресу, сохранённый проект тут не аргумент.
  const location = useLocation();
  const isOrgMode = isOrgTopLevelPath(location.pathname) || currentProject === null;
  const name = isOrgMode ? c.wholeOrganization : (currentProject?.name ?? '');

  // WEB-2910: каждый пункт меняет проект ДО смены адреса, поэтому сперва
  // спрашивает страницу через общую дверь `confirmLeave` («Сохранить / Не
  // сохранять / Остаться») и ничего не трогает при «Остаться».
  const handleProjectClick = async (projectId: string) => {
    setOpen(false);
    if (!(await confirmLeave())) return;
    const result = await dispatch(switchActiveProjectThunk(projectId));
    if (switchActiveProjectThunk.rejected.match(result)) {
      // WEB-1955: разобранный отказ уходит в реестр кодов целиком.
      message.error(localizeError(result.payload, s.projectSwitchFailed));
      return;
    }
    // WEB-407: после смены проекта — на посадку проекта (WEB-3748 юнит 3:
    // дашборд /home, было /funnels → /content).
    navigate(PROJECT_LANDING);
  };

  // WEB-499: вход в режим организации сохраняется на сервере; WEB-407 ①: адрес
  // идёт за режимом; WEB-2466: посадку режима решает одно правило orgModeLanding.
  const handleOrgModeClick = async () => {
    setOpen(false);
    if (!(await confirmLeave())) return;
    dispatch(clearActiveProjectThunk());
    navigate(orgModeLanding(orgRole));
  };

  // WEB-731: «Все проекты» — страница организации: входим в режим организации.
  const handleManageProjectsClick = async () => {
    setOpen(false);
    if (!(await confirmLeave())) return;
    dispatch(clearActiveProjectThunk());
    navigate('/projects');
  };

  const handleCreateClick = () => {
    setOpen(false);
    setWizardOpen(true);
  };

  const handleWizardClose = async (project?: Project) => {
    setWizardOpen(false);
    if (!project) return;
    // WEB-336: с организацией, чтобы не получить 400 в нескольких организациях.
    dispatch(fetchProjectsThunk(activeOrgId ?? undefined));
    // WEB-3760 юнит 2б (решение владельца 23.10 `proj-wizard-2310`): и из
    // режима организации, и из проекта — сразу в новый проект, на «Главную»
    // (там ход фонового разбора). Прежнее «остаёмся в организации, ведём на
    // /projects» (WEB-377/407③) снято. WEB-2912: вход в новый проект уводит с экрана, у которого может быть
    // несохранённый черновик; «остаться» — проект создан, но не входим.
    if (!(await confirmLeave())) return;
    dispatch(setCurrentProject(project));
    dispatch(switchActiveProjectThunk(project.id));
    navigate(PROJECT_LANDING);
  };

  return (
    <div className="qm-sw" ref={ref}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        title={name}
        data-testid="ctx"
      >
        <span className={'qm-ic' + (isOrgMode ? ' org' : '')} aria-hidden="true">
          {isOrgMode ? <Icon name="grid" size={14} /> : name.charAt(0).toUpperCase()}
        </span>
        <span className="nm">{name}</span>
        <Icon name="down" size={14} />
      </button>
      {open && (
        <div className="qm-pop" role="menu" data-testid="ctx-pop">
          {projects.length > 0 && (
            <input
              ref={search}
              className="input"
              autoComplete="off"
              data-testid="project-search"
              placeholder={c.shellFindProject}
              aria-label={c.shellFindProject}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          )}
          <div className="grp">
            {c.navProjects}
            {projects.length > 8 && <span className="n">{projects.length}</span>}
          </div>
          <div className="qm-pl">
            {projects.length > 0 && filteredProjects.length === 0 && (
              <div className="grp">{c.shellNothingFound}</div>
            )}
            {filteredProjects.map((project) => {
              const isActive = !isOrgMode && project.id === currentProject?.id;
              const act = () => void handleProjectClick(project.id);
              return (
                <div
                  key={project.id}
                  role="menuitemradio"
                  aria-checked={isActive}
                  tabIndex={0}
                  className="opt"
                  data-testid={`project-item-${project.id}`}
                  onClick={act}
                  onKeyDown={keyAct(act)}
                >
                  <span className="qm-ic" aria-hidden="true">
                    {project.name.charAt(0).toUpperCase()}
                  </span>
                  <span className="sp">{project.name}</span>
                  {isActive && (
                    <span data-testid={`project-active-${project.id}`}>
                      <Icon name="check" size={14} />
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <hr />
          {/* «Все проекты» — просмотр списка, правилом WEB-1556 не покрыто:
              гейт по орг-роли прежний. */}
          {isOrgAdmin && (
            <div
              role="menuitem"
              tabIndex={0}
              className="opt"
              data-testid="manage-projects-btn"
              onClick={() => void handleManageProjectsClick()}
              onKeyDown={keyAct(() => void handleManageProjectsClick())}
            >
              <span className="qm-ic plain" aria-hidden="true">
                <Icon name="panel" size={16} />
              </span>
              <span className="sp">{s.allProjects}</span>
            </div>
          )}
          <div
            role="menuitemradio"
            aria-checked={isOrgMode}
            tabIndex={0}
            className="opt"
            data-testid="project-org-mode"
            onClick={() => void handleOrgModeClick()}
            onKeyDown={keyAct(() => void handleOrgModeClick())}
          >
            <span className="qm-ic plain" aria-hidden="true">
              <Icon name="grid" size={16} />
            </span>
            <span className="sp">{c.wholeOrganization}</span>
            {isOrgMode && (
              <span data-testid="project-org-mode-active">
                <Icon name="check" size={14} />
              </span>
            )}
          </div>
          {/* WEB-1777 п.3: дверь видна всегда; у не-администратора погашена,
              достижима с клавиатуры (tabIndex) и называет причину строкой —
              текст из общего места, второй копии нет. Рабочая дверь
              aria-disabled не несёт (ревизия пачки 8). */}
          <div
            role="menuitem"
            tabIndex={0}
            className={'opt qm-new' + (isOrgAdmin ? '' : ' off')}
            aria-disabled={isOrgAdmin ? undefined : true}
            data-tip={isOrgAdmin ? undefined : createProjectDeniedHint()}
            data-testid="create-project-btn"
            onClick={isOrgAdmin ? handleCreateClick : undefined}
            onKeyDown={isOrgAdmin ? keyAct(handleCreateClick) : undefined}
          >
            <span className="qm-ic plain" aria-hidden="true">
              <Icon name="plus" size={16} />
            </span>
            <span className="sp">{s.createProject}</span>
          </div>
          {!isOrgAdmin && (
            <div className="qm-why" data-testid="create-project-denied-reason">
              {createProjectDeniedHint()}
            </div>
          )}
        </div>
      )}
      <CreateProjectWizard open={wizardOpen} onClose={handleWizardClose} />
    </div>
  );
}
